import { ReducerAuth } from "./slices/sliceAuth";
import { AnyAction, combineReducers, configureStore } from "@reduxjs/toolkit";
import { createSelectorHook, useDispatch } from "react-redux";
import { ReducerPatientTable } from "./slices/slicePatientTable";
import { ReducerCaregiverTable } from "./slices/sliceCaregiverTable";
import { ReducerAppointmentTable } from "./slices/sliceAppointmentTable";
import { ReducerAppointmentCalendar } from "./slices/sliceAppointmentCalendar";
import { ReducerUser } from "./slices/sliceUser";
import { StorageAuth, StorageUserData } from "../localStorage";
import { ReducerCompanyTable } from "./slices/sliceCompanyTable";
import { ReducerUsersTable } from "./slices/sliceUsersTable";
import { ReducerTaskTable } from "./slices/sliceTaskTable";
import { ReducerInvoiceTable } from "./slices/sliceInvoiceTable";
import { ReducerInvoiceFilter } from "./slices/sliceInvoiceFilter";
import { ReducerPatientActivityTable } from "./slices/slicePatientActivityTable";
import { ReducerCaregiverActivityTable } from "./slices/sliceCaregiverActivityTable";
import {
  ReducerFinanceCaregiverTable,
  ReducerFinancePatientTable,
} from "./slices/sliceFinanceTable";
import { ReducerUI } from "./slices/sliceUI";
import { ReducerCompany } from "./slices/sliceCompany";
import { ReducerInsuranceTable } from "./slices/sliceInsuranceTable";
import { ReducerInsuranceTableInReportingTab } from "./slices/sliceInsuranceTableOnReportingTab";
import { ReducerTaskListInReportingTab } from "./slices/sliceTaskListOnReportingTab";
import { ReducerCaregiversReport } from "./slices/sliceCaregiversReport";
import { ReducerModal } from "./slices/sliceModal";
import { ReducerBillingInvoiceTable } from "./slices/sliceBilling";
import { ReducerHHAConfigTable } from "./slices/sliceHHAConfigTable";

const RootReducer = combineReducers({
  ui: ReducerUI,
  user: ReducerUser,
  auth: ReducerAuth,
  company: ReducerCompany,
  taskTable: ReducerTaskTable,
  usersTable: ReducerUsersTable,
  companyTable: ReducerCompanyTable,
  invoiceTable: ReducerInvoiceTable,
  patientTable: ReducerPatientTable,
  InvoiceFilter: ReducerInvoiceFilter,
  caregiverTable: ReducerCaregiverTable,
  insuranceTable: ReducerInsuranceTable,
  insuranceTableInReportingTab: ReducerInsuranceTableInReportingTab,
  taskListTableInReportingTab: ReducerTaskListInReportingTab,
  appointmentTable: ReducerAppointmentTable,
  appointmentCalendar: ReducerAppointmentCalendar,
  financePatientTable: ReducerFinancePatientTable,
  patientActivityTable: ReducerPatientActivityTable,
  financeCaregiverTable: ReducerFinanceCaregiverTable,
  caregiverActivityTable: ReducerCaregiverActivityTable,
  caregiversReportTable: ReducerCaregiversReport,
  billingInvoiceTable: ReducerBillingInvoiceTable,
  hhaConfigTable: ReducerHHAConfigTable,
  modal: ReducerModal,
});

type ReducerParams = Parameters<typeof RootReducer>;
type ReducerReturn = ReturnType<typeof RootReducer>;

const ActionAppTypeResetStore = "RESET_APP_REDUX_STORE";

export const ActionApp = {
  ResetStore: (): AnyAction => ({ type: ActionAppTypeResetStore }),
};

const AppReducer = (...arg: ReducerParams): ReducerReturn => {
  const [, action] = arg;
  if (action.type === ActionAppTypeResetStore) {
    StorageUserData.Clear();
    StorageAuth.Clear();
    return RootReducer(undefined, action);
  }
  return RootReducer(...arg);
};

export const EvvAppStore = configureStore({
  reducer: AppReducer,
  devTools: process.env.NODE_ENV !== "production",
});

export type EvvAppState = ReturnType<typeof RootReducer>;
export type EvvAppDispatch = typeof EvvAppStore.dispatch;
export const useAppDispatch = () => useDispatch<EvvAppDispatch>();
export const useAppSelector = createSelectorHook<EvvAppState>();
